import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileWarning, X, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin-only EVV CSV import modal. Mounted from the Timesheets page.
 *
 * Endpoint: POST /api/timesheets/import (multipart/form-data, single .csv).
 * Posts the selected CSV file, displays the structured summary in a
 * results panel, and calls onImported() so the parent can reload the
 * table in-place.
 *
 * The user never sees raw stack traces — only the summary counts plus
 * up to 10 sample unmatched StaffID / ServiceName values so mapping
 * tweaks can be made without re-uploading.
 */
export default function EvvImportModal({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);

  const reset = () => {
    setFile(null);
    setSummary(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    setSummary(null);
    if (!f) { setFile(null); return; }
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files are accepted");
      e.target.value = "";
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB; split into smaller files");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/timesheets/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setSummary(data);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} EVV ${data.imported === 1 ? "visit" : "visits"}`);
        onImported?.();
      } else {
        toast.info("No new visits imported — see results below");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const close = (v) => {
    if (uploading) return;
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="evv-import-modal"
        onEscapeKeyDown={(e) => uploading && e.preventDefault()}
        onPointerDownOutside={(e) => uploading && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary" style={{ fontFamily: "Outfit" }}>
            <Upload size={18} /> Import EVV visits
          </DialogTitle>
          <DialogDescription className="text-sm">
            Upload the Maryland DDA <span className="font-mono">Services Rendered / Advanced EVV Detail Report</span> CSV.
            Only <span className="font-semibold">Closed</span> visits will be imported; Ready / Open / other statuses are reported and skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label
            className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              file ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary/30 hover:bg-accent/30"
            }`}
            data-testid="evv-import-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              disabled={uploading}
              className="hidden"
              data-testid="evv-import-file-input"
            />
            <Upload className="mx-auto mb-2 text-muted-foreground" size={28} />
            <div className="text-sm font-semibold text-primary">
              {file ? file.name : "Click to choose a CSV file"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {file ? `${(file.size / 1024).toFixed(1)} KB` : ".csv up to 25 MB"}
            </div>
          </label>

          {file && !summary && (
            <button
              onClick={upload}
              disabled={uploading}
              className="accp-btn-primary w-full py-3 text-base font-semibold disabled:opacity-50"
              data-testid="evv-import-upload-btn"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? "Processing…" : "Upload & process"}
            </button>
          )}

          {summary && <ImportResults summary={summary} onReset={reset} onImported={onImported} />}
        </div>

        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => close(false)}
            disabled={uploading}
            className="accp-btn-ghost"
            data-testid="evv-import-close-btn"
          >
            <X size={14} /> Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportResults({ summary, onReset, onImported }) {
  const isError = (summary.errors?.length || 0) > 0 && summary.imported === 0;
  const isWarn = summary.unresolved_staff > 0 || summary.invalid > 0 || summary.unmatched_services > 0;

  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="evv-import-results">
      <div className={`p-4 ${isError ? "bg-[#FFEBEE]" : isWarn ? "bg-[#FFF8E6]" : "bg-[#DCF5D9]"}`}>
        <div className="flex items-center gap-2 text-sm font-semibold">
          {isError ? <AlertTriangle size={16} className="text-[#B71C1C]" />
           : isWarn ? <FileWarning size={16} className="text-[#8A5A00]" />
           : <CheckCircle2 size={16} className="text-[#2C5C0E]" />}
          {isError ? "Import failed" : isWarn ? "Import completed with warnings" : "Import successful"}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 text-xs">
        <Tile label="Imported"          value={summary.imported}          tone="good"  testId="evv-stat-imported" />
        <Tile label="Duplicates"        value={summary.duplicates}        tone="muted" testId="evv-stat-duplicates" />
        <Tile label="Skipped (status)"  value={summary.skipped_status}    tone="muted" testId="evv-stat-skipped-status" />
        <Tile label="Unresolved staff (⚪)"  value={summary.unresolved_staff}  tone={summary.unresolved_staff ? "warn" : "muted"} testId="evv-stat-unresolved-staff" />
        <Tile label="Unmatched service" value={summary.unmatched_services} tone={summary.unmatched_services ? "warn" : "muted"} testId="evv-stat-unmatched-services" />
        <Tile label="Invalid"           value={summary.invalid}           tone={summary.invalid ? "warn" : "muted"} testId="evv-stat-invalid" />
      </div>

      {summary.sample_unmatched_staff?.length > 0 && (
        <UnresolvedStaffList
          items={summary.sample_unmatched_staff}
          onImported={onImported}
        />
      )}
      {summary.sample_unmatched_services?.length > 0 && (
        <SampleList
          title="Sample unmatched ServiceName values (imported with Missing Note)"
          items={summary.sample_unmatched_services}
          testId="evv-sample-unmatched-services"
        />
      )}
      {summary.sample_skipped_statuses?.length > 0 && (
        <SampleList
          title="Skipped ServiceStatus values (only Closed is imported)"
          items={summary.sample_skipped_statuses}
          testId="evv-sample-skipped-statuses"
        />
      )}
      {summary.errors?.length > 0 && (
        <div className="border-t border-border bg-[#FFEBEE]/40 p-3" data-testid="evv-errors-list">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#B71C1C] mb-1.5">
            Errors ({summary.errors.length})
          </div>
          <ul className="text-xs text-foreground/80 max-h-32 overflow-y-auto space-y-0.5 list-disc list-inside">
            {summary.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
            {summary.errors.length > 20 && <li className="text-muted-foreground">… and {summary.errors.length - 20} more</li>}
          </ul>
        </div>
      )}

      <div className="border-t border-border p-3 flex justify-end">
        <button onClick={onReset} className="accp-btn-ghost text-xs" data-testid="evv-import-another-btn">
          <Upload size={13} /> Import another file
        </button>
      </div>
    </div>
  );
}

function Tile({ label, value, tone, testId }) {
  const toneClass = tone === "good"  ? "ring-[#A0E08A] bg-[#F4FCE9] text-[#2C5C0E]"
                  : tone === "warn"  ? "ring-[#FFE4A0] bg-[#FFF8E6] text-[#8A5A00]"
                  :                    "ring-border bg-white text-foreground";
  return (
    <div className={`rounded-lg p-2.5 ring-1 ${toneClass}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function SampleList({ title, items, testId }) {
  return (
    <div className="border-t border-border p-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
        {title}
      </div>
      <ul className="text-xs text-foreground/80 space-y-0.5 list-disc list-inside font-mono">
        {items.map((v, i) => <li key={i}>{v}</li>)}
      </ul>
    </div>
  );
}

/**
 * Unresolved-staff list with a Resolve action per row.
 *
 * The importer only surfaces a StaffID in `sample_unmatched_staff` when
 * an *imported* row actually referenced that value AND no staff member
 * in our directory has that value in their `external_staff_id`. Absence
 * alone (a staff member with no EVV Staff ID) is a valid operational
 * state and never warns here.
 *
 * "Resolve" opens the Staff directory pre-filtered to that raw value so
 * the admin can pick the correct staff member and link the ID from
 * their profile — no manual searching, no ID copy-paste error.
 */
function UnresolvedStaffList({ items, onImported }) {
  const navigate = useNavigate();
  return (
    <div className="border-t border-border p-3" data-testid="evv-sample-unmatched-staff">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
        Not linked (⚪) — imported StaffID values with no matching staff link
      </div>
      <ul className="text-xs space-y-1.5">
        {items.map((v, i) => (
          <li key={i}
              className="flex items-center justify-between gap-2 bg-slate-50 rounded px-2 py-1.5">
            <span className="font-mono text-foreground/80 break-all">{v}</span>
            <button
              className="accp-btn-ghost text-[11px] shrink-0"
              onClick={() => {
                navigate(`/staff?evv=${encodeURIComponent(v)}`);
                onImported && onImported();
              }}
              data-testid={`evv-resolve-staff-${i}`}
              title="Open the Staff directory to link this EVV Staff ID"
            >
              Resolve <ExternalLink size={11} />
            </button>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground mt-2">
        Click <span className="font-medium">Resolve</span> to open the Staff
        directory and link this EVV Staff ID to the correct person. Staff who
        are Not linked will simply be skipped — no warning is generated unless
        an imported EVV row actually references them.
      </p>
    </div>
  );
}

